import React, { useState, useEffect } from 'react';
import { formatDistanceToNow } from 'date-fns';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { commentService } from '../../../services/commentService';
import { teamService } from '../../../services/teamService';
import { useAuth } from '../../../contexts/AuthContext';

const CommentThread = ({ recordType, recordId }) => {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [teamMembers, setTeamMembers] = useState([]);
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [mentionSearch, setMentionSearch] = useState('');
  const [mentionedUsers, setMentionedUsers] = useState([]);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editContent, setEditContent] = useState('');

  const handleRealtimeUpdate = (payload) => {
    if (payload?.eventType === 'INSERT') {
      setComments(prev => [payload?.comment, ...prev]);
    } else if (payload?.eventType === 'UPDATE') {
      setComments(prev =>
        prev?.map(c => (c?.id === payload?.comment?.id ? payload?.comment : c))
      );
    } else if (payload?.eventType === 'DELETE') {
      setComments(prev => prev?.filter(c => c?.id !== payload?.commentId));
    }
  };

  useEffect(() => {
    loadComments();
    loadTeamMembers();

    // Subscribe to real-time updates
    const unsubscribe = commentService?.subscribeToComments(
      recordType,
      recordId,
      handleRealtimeUpdate
    );

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [recordType, recordId]);

  const loadComments = async () => {
    try {
      setIsLoading(true);
      const data = await commentService?.getComments(recordType, recordId);
      setComments(data || []);
    } catch (err) {
      console.error('Error loading comments:', err);
      setError('Failed to load comments');
    } finally {
      setIsLoading(false);
    }
  };

  const loadTeamMembers = async () => {
    try {
      const members = await teamService?.getTeamMembers();
      setTeamMembers(members || []);
    } catch (err) {
      console.error('Error loading team members:', err);
    }
  };

  const handleCommentChange = (e) => {
    const value = e?.target?.value;
    setNewComment(value);

    // Check for @ mention
    const lastAtIndex = value?.lastIndexOf('@');
    if (lastAtIndex !== -1) {
      const searchTerm = value?.substring(lastAtIndex + 1);
      if (searchTerm?.length === 0 || /^[a-zA-Z0-9]*$/?.test(searchTerm)) {
        setMentionSearch(searchTerm);
        setShowMentionDropdown(true);
      } else {
        setShowMentionDropdown(false);
      }
    } else {
      setShowMentionDropdown(false);
    }
  };

  const handleMentionSelect = (member) => {
    const lastAtIndex = newComment?.lastIndexOf('@');
    const beforeMention = newComment?.substring(0, lastAtIndex);
    const afterMention = newComment?.substring(lastAtIndex + mentionSearch?.length + 1);
    const updatedComment = `${beforeMention}@${member?.userProfile?.fullName} ${afterMention}`;

    setNewComment(updatedComment);
    setShowMentionDropdown(false);

    // Add to mentioned users if not already present
    if (!mentionedUsers?.includes(member?.userId)) {
      setMentionedUsers([...mentionedUsers, member?.userId]);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!newComment?.trim()) return;

    try {
      setIsSubmitting(true);
      setError('');

      await commentService?.createComment({
        recordType,
        recordId,
        content: newComment,
        mentionedUsers
      });

      setNewComment('');
      setMentionedUsers([]);
    } catch (err) {
      console.error('Error creating comment:', err);
      setError('Failed to post comment');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (comment) => {
    setEditingCommentId(comment?.id);
    setEditContent(comment?.content);
  };

  const handleUpdate = async (commentId) => {
    if (!editContent?.trim()) return;

    try {
      await commentService?.updateComment(commentId, editContent);
      setEditingCommentId(null);
      setEditContent('');
    } catch (err) {
      console.error('Error updating comment:', err);
      setError('Failed to update comment');
    }
  };

  const handleDelete = async (commentId) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) return;

    try {
      await commentService?.deleteComment(commentId);
    } catch (err) {
      console.error('Error deleting comment:', err);
      setError('Failed to delete comment');
    }
  };

  const filteredTeamMembers = teamMembers?.filter(member =>
    member?.userProfile?.fullName
      ?.toLowerCase()
      ?.includes(mentionSearch?.toLowerCase())
  );

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center gap-2 mb-4">
        <Icon name="MessageSquare" className="w-5 h-5 text-accent" />
        <h3 className="text-lg font-semibold text-gray-900">Comments & Insights</h3>
        <span className="ml-auto text-sm text-gray-500">{comments?.length} comments</span>
      </div>

      {/* Comment Form */}
      <form onSubmit={handleSubmit} className="mb-6">
        <div className="relative">
          <textarea
            value={newComment}
            onChange={handleCommentChange}
            placeholder="Share insights or tag colleagues with @..."
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent resize-none"
            rows={3}
            disabled={isSubmitting}
          />

          {/* Mention Dropdown */}
          {showMentionDropdown && filteredTeamMembers?.length > 0 && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
              {filteredTeamMembers?.map(member => (
                <button
                  key={member?.userId}
                  type="button"
                  onClick={() => handleMentionSelect(member)}
                  className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-2"
                >
                  <Icon name="User" className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-900">{member?.userProfile?.fullName}</span>
                  <span className="text-xs text-gray-500 ml-auto">{member?.position}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {error && (
          <p className="mt-2 text-sm text-error">{error}</p>
        )}

        <div className="flex items-center justify-between mt-3">
          <p className="text-xs text-gray-500">
            Use @ to mention team members
          </p>
          <Button
            type="submit"
            disabled={isSubmitting || !newComment?.trim()}
            className="px-4 py-2"
          >
            {isSubmitting ? 'Posting...' : 'Post Comment'}
          </Button>
        </div>
      </form>

      {/* Comments List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <Icon name="Loader" className="w-6 h-6 text-accent animate-spin" />
        </div>
      ) : comments?.length === 0 ? (
        <div className="text-center py-8">
          <Icon name="MessageSquare" className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No comments yet. Be the first to share insights!</p>
        </div>
      ) : (
        <div className="space-y-4">
          {comments?.map(comment => (
            <div key={comment?.id} className="border-l-2 border-accent/20 pl-4 py-2">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-accent/10 flex items-center justify-center">
                    <Icon name="User" className="w-4 h-4 text-accent" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {comment?.userProfile?.fullName || 'Unknown User'}
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatDistanceToNow(new Date(comment?.createdAt), { addSuffix: true })}
                    </p>
                  </div>
                </div>

                {user?.id === comment?.userId && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleEdit(comment)}
                      className="text-gray-400 hover:text-accent"
                    >
                      <Icon name="Edit" className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(comment?.id)}
                      className="text-gray-400 hover:text-error"
                    >
                      <Icon name="Trash2" className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>

              {editingCommentId === comment?.id ? (
                <div className="mt-2">
                  <textarea
                    value={editContent}
                    onChange={(e) => setEditContent(e?.target?.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent resize-none"
                    rows={2}
                  />
                  <div className="flex gap-2 mt-2">
                    <Button
                      onClick={() => handleUpdate(comment?.id)}
                      className="px-3 py-1 text-sm"
                    >
                      Save
                    </Button>
                    <Button
                      onClick={() => setEditingCommentId(null)}
                      className="px-3 py-1 text-sm bg-gray-200 text-gray-700 hover:bg-gray-300"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{comment?.content}</p>
              )}

              {comment?.mentionedUsers?.length > 0 && (
                <div className="flex items-center gap-2 mt-2">
                  <Icon name="AtSign" className="w-3 h-3 text-gray-400" />
                  <span className="text-xs text-gray-500">
                    {comment?.mentionedUsers?.length} {comment?.mentionedUsers?.length === 1 ? 'person' : 'people'} mentioned
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CommentThread;